import {
  Component,
  OnInit,
  ElementRef,
  inject,
  viewChildren,
  signal,
  output,
  effect,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { 
  MatDialogModule, 
  MatDialogRef, 
  MAT_DIALOG_DATA 
} from '@angular/material/dialog';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { ButtonComponent } from '../button/button.component';

export interface OtpDialogData {
  title?: string;
  subtitle?: string;
  error?: string | null;
  isLoading?: boolean;
}

@Component({
  selector: 'app-otp-verification',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    MatIconModule, 
    MatDialogModule, 
    MatButtonModule,
    LoadingSpinnerComponent,
    ButtonComponent,
  ],
  templateUrl: './otp-verification-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./otp-verification-dialog.component.scss'],
})
export class OtpVerificationDialogComponent implements OnInit {
  readonly inputElements = viewChildren<ElementRef>('codeInput');
  readonly submitOtp = output<string>();

  private readonly dialogRef = inject(MatDialogRef<OtpVerificationDialogComponent>);
  private readonly fb = inject(FormBuilder);
  readonly data = inject<OtpDialogData>(MAT_DIALOG_DATA);
  
  readonly isLoading = signal(this.data.isLoading ?? false);
  readonly errorMessage = signal<string | null>(this.data.error ?? null);
  readonly title = signal<string>(this.data.title ?? 'Verification');
  readonly subtitle = signal<string>(this.data.subtitle ?? 'Enter the code shown in your authenticator app');

  otpForm!: FormGroup;
  readonly codeLength = 6;

  constructor() {
    effect(() => {
      const inputs = this.inputElements();
      if (inputs.length > 0) {
        this.focusFirstInput();
      }
    });
  }

  ngOnInit(): void {
    this.otpForm = this.fb.group({
      codeDigits: this.fb.array(
        Array(this.codeLength)
          .fill('')
          .map(() =>
            this.fb.control('', [
              Validators.required,
              Validators.pattern(/^\d$/),
            ]),
          ),
      ),
    });
  }

  get codeDigits(): FormArray {
    return this.otpForm.get('codeDigits') as FormArray;
  }

  get submitButtonState(): 'loading' | 'disabled' | 'default' {
    if (this.isLoading()) {
      return 'loading';
    }

    return this.otpForm.invalid ? 'disabled' : 'default';
  }

  onCodeInput(event: KeyboardEvent, index: number): void {
    if (this.errorMessage()) {
      this.errorMessage.set(null);
    }

    const inputEl = event.target as HTMLInputElement;
    const currentValue = inputEl.value;
    const isDigit = /^\d$/.test(currentValue);

    if (event.key === 'Tab') return;

    if (currentValue && !isDigit) {
      this.codeDigits.at(index).setValue('');
      return;
    }

    if (event.key === 'Backspace') {
      if (!currentValue && index > 0) this.focusInput(index - 1);
    } else if (isDigit && index < this.codeLength - 1) {
      this.focusInput(index + 1);
    }
  }

  handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text').trim();

    if (pastedData && /^\d+$/.test(pastedData)) {
      const digits = pastedData.slice(0, this.codeLength).split('');
      digits.forEach((digit, i) => {
        if (this.codeDigits.at(i)) this.codeDigits.at(i).setValue(digit);
      });
      const focusIndex = Math.min(digits.length, this.codeLength - 1);
      this.focusInput(focusIndex);
      
      if (digits.length === this.codeLength) {
         this.onSubmit();
      }
    }
  }

  focusInput(index: number): void {
    const elements = this.inputElements();
    if (elements[index]) {
      elements[index].nativeElement.focus();
      elements[index].nativeElement.select();
    }
  }

  private focusFirstInput(): void {
    this.focusInput(0);
  }

  onSubmit(): void {
    if (this.otpForm.invalid || this.isLoading()) {
      this.otpForm.markAllAsTouched();
      return;
    }

    const otp = this.codeDigits.value.join('');
    
    this.submitOtp.emit(otp);
  }

  onCancel(): void {
    if (!this.isLoading()) {
      this.dialogRef.close();
    }
  }
  
  setLoadingState(loading: boolean) {
    this.isLoading.set(loading);
  }
  
  setErrorState(error: string) {
    this.errorMessage.set(error);
  }
}
